import type {IntlShape} from 'react-intl';

import type {FileInfo} from '../../../utils/attachments/types';
import {FILE_TYPES, IMAGE_TYPE_GIF} from '../../../utils/attachments/constants';
import {getFileType} from '../../../utils/attachments/utils';
import {FileAttachment} from '../../file-attachment';
import {SingleImageView} from '../../single-image-view';

import styles from './styles.module.css';

export type Props = {
    postId: string;
    intl: IntlShape;
    compactDisplay?: boolean;
    enableSVGs?: boolean;
    fileInfos: FileInfo[];
    fileIds: string[];
    isInPermalink?: boolean;
    interactive?: boolean;
    isEmbedVisible?: boolean;
    canDownloadFiles?: boolean;
    enablePublicLink?: boolean;
    renderMenuItems?: (fileInfo: FileInfo) => React.ReactNode;
    getFileUrl: (id: string) => string;
    getThumbnailUrl: (id: string) => string;
    getDownloadUrl: (id: string) => string;
    getFilePublicLink?: (fileId: string) => Promise<string>;
    getFilePreviewUrl: (id: string) => string;
    toggleEmbedVisibility?: (postId: string) => void;
    handleAttachmentClick?: (fileInfo: FileInfo) => void;
    handleCopyToClipboard: (src: string) => void;
    onDownloadAttachment?: (fileInfo: FileInfo) => void;
};

type Preview = {
    data: FileInfo;
};

export const FileAttachmentsList = (props: Props) => {
    const {
        intl,
        postId,
        compactDisplay,
        enableSVGs,
        fileInfos,
        fileIds,
        isInPermalink,
        handleAttachmentClick,
        interactive,
        toggleEmbedVisibility,
        isEmbedVisible,
        enablePublicLink,
        renderMenuItems,
        getFileUrl,
        getThumbnailUrl,
        getDownloadUrl,
        canDownloadFiles,
        handleCopyToClipboard,
        getFilePublicLink,
        getFilePreviewUrl,
        onDownloadAttachment,
    } = props;
    const {formatMessage} = intl;
    const fileInfoMap = new Map(fileInfos.map((f) => [f.id, f]));
    const previews: Preview[] = fileIds
        .map((id) => ({data: fileInfoMap.get(id)}))
        .filter((p): p is Preview => Boolean(p.data));

    const handleImageClick = (fileInfo: FileInfo) => {
        if (!interactive) {
            return;
        }

        handleAttachmentClick?.(fileInfo);
    };
    const handleToggleEmbedVisibility = () => {
        toggleEmbedVisibility?.(postId);
    };
    const interactiveFileTypes = {
        [FILE_TYPES.VIDEO]: true,
        [FILE_TYPES.AUDIO]: true,
        [FILE_TYPES.IMAGE]: true,
        [FILE_TYPES.PDF]: true,
        [FILE_TYPES.SVG]: Boolean(enableSVGs),
    };
    const content = previews.map(({data}) => {
        const fileType = getFileType(data.extension);
        const isImageWithPreview = fileType === FILE_TYPES.IMAGE && data.has_preview_image;
        const isSVGEnabled = fileType === FILE_TYPES.SVG && enableSVGs;
        const isGIF = data.extension === IMAGE_TYPE_GIF;
        const isSingleAttachment = previews.length === 1;

        const onDownload = onDownloadAttachment ? () => onDownloadAttachment(data) : undefined;

        if (isSingleAttachment && (isImageWithPreview || isGIF || isSVGEnabled)) {
            return (
                <SingleImageView
                    key={data.id}
                    className={styles.singleImageView}
                    fileInfo={fileInfos[0] as FileInfo}
                    isEmbedVisible={isEmbedVisible}
                    compactDisplay={compactDisplay}
                    isInPermalink={isInPermalink}
                    interactive={interactive}
                    enablePublicLink={enablePublicLink}
                    canDownloadFile={canDownloadFiles}
                    getFileUrl={getFileUrl}
                    toggleEmbedVisibility={handleToggleEmbedVisibility}
                    handleImageClick={handleImageClick}
                    onCopyToClipboard={handleCopyToClipboard}
                    getFilePreviewUrl={getFilePreviewUrl}
                    getFilePublicLink={getFilePublicLink}
                    onDownload={onDownload}
                />
            );
        }

        const ariaLabel = `${formatMessage({id: 'file_attachment.thumbnail', defaultMessage: 'file thumbnail'})} ${
            data.name
        }`.toLowerCase();

        return (
            <FileAttachment
                key={data.id}
                fileInfo={data}
                interactive={interactive && interactiveFileTypes[fileType]}
                handleAttachmentClick={handleImageClick}
                compactDisplay={compactDisplay}
                ariaLabelText={ariaLabel}
                canDownloadFiles={canDownloadFiles}
                enableSVGs={enableSVGs}
                renderMenuItems={renderMenuItems}
                getFileUrl={getFileUrl}
                getThumbnailUrl={getThumbnailUrl}
                getDownloadUrl={getDownloadUrl}
                tooltipText={formatMessage({id: 'view_image_popover.download', defaultMessage: 'Download'})}
                onDownload={onDownload}
            />
        );
    });

    return <div className={styles.fileAttachmentsList} data-testid='file-attachments-list'>{content}</div>;
};
